import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('googleSignInUtils');

export interface GoogleSignInConfig {
  clientId: string;
  onCredentialResponse: (response: GoogleCredentialResponse) => void;
  onError?: (error: string) => void;
  onSuccess?: () => void;
  autoPrompt?: boolean;
  buttonConfig?: GoogleButtonConfiguration;
}

// Type aliases for Google Sign In types
type GoogleCredentialResponse = {
  credential: string;
  select_by?: string;
  client_id?: string;
};

type GoogleButtonConfiguration = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string | number;
  locale?: string;
  click_listener?: () => void;
};

type GooglePromptMomentNotification = {
  isDisplayMoment(): boolean;
  isDisplayed(): boolean;
  isNotDisplayed(): boolean;
  getNotDisplayedReason(): string;
  isSkippedMoment(): boolean;
  getSkippedReason(): string;
  isDismissedMoment(): boolean;
  getDismissedReason(): string;
  getMomentType(): string;
};

export interface GoogleSignInInstance {
  renderButton: (containerId: string) => boolean;
  prompt: () => void;
  isInitialized: () => boolean;
  destroy: () => void;
}

class GoogleSignIn implements GoogleSignInInstance {
  private config: GoogleSignInConfig;
  private initialized = false;
  private buttonContainerId?: string;
  private observer?: MutationObserver;

  constructor(config: GoogleSignInConfig) {
    this.config = config;
  }

  initialize(): void {
    const debug = typeof localStorage !== 'undefined' && localStorage.getItem('debugGoogle') === 'true';

    if (!window.google?.accounts?.id) {
      logger?.error('Google Sign In API not loaded');
      if (debug) console.error('[Google OneTap] window.google.accounts.id is not available');
      return;
    }

    if (debug) {
      console.log('[Google OneTap] Initializing with config:', {
        clientId: this.config.clientId,
        autoPrompt: this.config.autoPrompt,
        hasCallback: !!this.config.onCredentialResponse,
        hasOnError: !!this.config.onError,
        hasOnSuccess: !!this.config.onSuccess,
      });
    }

    try {
      window.google.accounts.id.initialize({
        client_id: this.config.clientId,
        callback: this.config.onCredentialResponse,
      });

      this.initialized = true;

      if (debug) console.log('[Google OneTap] ✅ Initialization successful');

      if (this.config.autoPrompt !== false) {
        if (debug) console.log('[Google OneTap] Auto-prompt enabled, calling prompt()...');
        this.prompt();
      } else {
        if (debug) console.log('[Google OneTap] Auto-prompt disabled');
      }

      if (this.config.onSuccess) {
        this.config.onSuccess();
      }
    } catch (error) {
      logger?.error('Failed to initialize Google Sign In:', error);
      if (debug) console.error('[Google OneTap] ❌ Initialization error:', error);
      if (this.config.onError) {
        this.config.onError('Failed to initialize Google Sign In');
      }
    }
  }

  renderButton(containerId: string): boolean {
    const debug = typeof localStorage !== 'undefined' && localStorage.getItem('debugGoogle') === 'true';

    if (!this.initialized) {
      logger?.error('Google Sign In not initialized');
      if (debug) console.error('[Google OneTap] Cannot render button - not initialized');
      return false;
    }

    // Multiple PromptLogin instances may render the same container ID (e.g., mobile + desktop
    // on the login page). getElementById returns the first match, which may be CSS-hidden.
    // Find the first visible container instead.
    const containers = document.querySelectorAll(`#${containerId}`);
    const container = (Array.from(containers).find((el) => (el as HTMLElement).offsetParent !== null) ??
      containers[0]) as HTMLElement | null;
    if (!container) {
      logger?.error(`Container with id "${containerId}" not found`);
      if (debug) console.error(`[Google OneTap] Button container "#${containerId}" not found in DOM`);
      return false;
    }

    if (debug) {
      console.log('[Google OneTap] Rendering button:', {
        containerId,
        containerExists: !!container,
        config: this.config.buttonConfig || 'default',
      });
    }

    try {
      this.buttonContainerId = containerId;

      // Hide any existing fallback button
      const existingButton = container.querySelector('.PromptEmail-siwg-default-button') as HTMLElement;
      if (existingButton) {
        existingButton.style.display = 'none';
        if (debug) console.log('[Google OneTap] Hid existing fallback button');
      }

      // Render the Google button
      window.google.accounts!.id.renderButton(
        container,
        this.config.buttonConfig || {
          size: 'large',
          text: 'signin_with',
          theme: 'filled_blue',
          width: container.offsetWidth.toString(),
        },
      );

      // Set up observer to detect when iframe is rendered
      this.observeButtonRendering(container);

      logger?.info('Google Sign In button render initiated');
      if (debug) console.log('[Google OneTap] ✅ renderButton() called successfully, waiting for iframe...');
      return true;
    } catch (error) {
      logger?.error('Failed to render Google Sign In button:', error);
      if (debug) console.error('[Google OneTap] ❌ renderButton() error:', error);
      return false;
    }
  }

  private observeButtonRendering(container: HTMLElement): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node: Node) => {
          if (node.nodeName === 'IFRAME' || (node instanceof Element && node.querySelector('iframe'))) {
            logger?.info('Google button iframe rendered successfully');
            window.dispatchEvent(new Event('google-button-rendered'));
          }
        });
      });
    });

    this.observer.observe(container, {
      childList: true,
      subtree: true,
    });

    // Also check after a short delay
    setTimeout(() => {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        logger?.info('Google button iframe confirmed');
        window.dispatchEvent(new Event('google-button-rendered'));
      }
    }, 100);
  }

  prompt(): void {
    const debug = typeof localStorage !== 'undefined' && localStorage.getItem('debugGoogle') === 'true';

    if (!this.initialized) {
      logger?.error('Google Sign In not initialized');
      if (debug) console.error('[Google OneTap] Cannot call prompt() - not initialized');
      return;
    }

    if (debug) console.log('[Google OneTap] Calling window.google.accounts.id.prompt()...');

    try {
      window.google.accounts!.id.prompt((notification: GooglePromptMomentNotification) => {
        const momentType = notification.getMomentType();

        if (debug) {
          console.log('[Google OneTap] Prompt moment notification:', {
            momentType,
            isDisplayMoment: notification.isDisplayMoment?.(),
            isDisplayed: notification.isDisplayed?.(),
            isNotDisplayed: notification.isNotDisplayed?.(),
            isDismissedMoment: notification.isDismissedMoment?.(),
            isSkippedMoment: notification.isSkippedMoment?.(),
          });
        }

        if (notification.isNotDisplayed?.()) {
          const reason = notification.getNotDisplayedReason?.();
          if (debug) {
            console.log('[Google OneTap] ⚠️ Prompt NOT displayed. Reason:', reason);
          }
          logger?.info(`Google OneTap not displayed: ${reason}`);
        }

        if (notification.isSkippedMoment?.()) {
          const reason = notification.getSkippedReason?.();
          if (debug) {
            console.log('[Google OneTap] ⚠️ Prompt skipped. Reason:', reason);
          }
          logger?.info(`Google OneTap skipped: ${reason}`);
        }

        if (notification.isDismissedMoment?.()) {
          const reason = notification.getDismissedReason?.();
          if (debug) {
            console.log('[Google OneTap] 👋 Prompt dismissed by user. Reason:', reason);
          }
          // User clicked X to dismiss the prompt
          // Store dismissal timestamp for cooldown period
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('google_onetap_dismissed', Date.now().toString());
            if (debug) console.log('[Google OneTap] Saved dismissal timestamp for 24h cooldown');
          }
        }

        if (notification.isDisplayed?.()) {
          if (debug) console.log('[Google OneTap] ✅ Prompt displayed successfully');
        }
      });

      if (debug) console.log('[Google OneTap] prompt() call completed without throwing');
    } catch (error) {
      logger?.error('Failed to show Google One Tap prompt:', error);
      if (debug) console.error('[Google OneTap] ❌ prompt() threw error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    if (window.google?.accounts?.id) {
      window.google.accounts!.id.cancel();
    }

    this.initialized = false;
  }
}

let instance: GoogleSignIn | null = null;

export const createGoogleSignIn = (config: GoogleSignInConfig): GoogleSignInInstance => {
  if (instance) {
    instance.destroy();
  }

  instance = new GoogleSignIn(config);
  instance.initialize();

  return instance;
};

export const getGoogleSignIn = (): GoogleSignInInstance | null => {
  return instance;
};

export const waitForGoogleApi = (): Promise<void> => {
  const debug = typeof localStorage !== 'undefined' && localStorage.getItem('debugGoogle') === 'true';

  return new Promise((resolve) => {
    if (window.google?.accounts?.id) {
      if (debug) console.log('[Google OneTap] Google API already loaded');
      resolve();
      return;
    }

    if (debug) console.log('[Google OneTap] Waiting for Google API to load...');

    let attempts = 0;
    const checkInterval = setInterval(() => {
      attempts++;
      if (window.google?.accounts?.id) {
        clearInterval(checkInterval);
        if (debug) console.log(`[Google OneTap] ✅ Google API loaded after ${attempts * 100}ms`);
        resolve();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.google?.accounts?.id) {
        if (debug) console.error('[Google OneTap] ⚠️ Timeout waiting for Google API after 10s');
        logger?.error('Google API did not load within 10 seconds');
      }
      resolve();
    }, 10000);
  });
};

/**
 * Decode Google JWT credential to extract user information
 * @param credential - JWT token from Google Sign In
 * @returns Object containing email and other user info, or null if decode fails
 */
export const decodeGoogleCredential = (credential: string): { email?: string; name?: string } | null => {
  try {
    // JWT format: header.payload.signature
    const parts = credential.split('.');
    if (parts.length !== 3) {
      logger?.warn('Invalid JWT format');
      return null;
    }

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    return {
      email: payload.email,
      name: payload.name,
    };
  } catch (error) {
    logger?.error('Failed to decode Google credential:', error);
    return null;
  }
};
